import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
  ToolbarPopoverSwitchRow,
  type ToolbarPopoverButtonProps,
  type ToolbarPopoverChoiceOption,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  useEffectiveHanziScript,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import type { ReactNode } from "react";

const ACCESSIBILITY_LABEL = "Display options";

const SCRIPT_OPTIONS: readonly ToolbarPopoverChoiceOption<HanziScript>[] = [
  { label: "Simplified", testID: "display-script-simplified", value: "simplified" },
  { label: "Traditional", testID: "display-script-traditional", value: "traditional" },
];

export type PrimaryControlsStripExpansionDisplayPushButtonProps = {
  /**
   * Page-local display rows appended after the shared tone-colors / script controls (e.g. Words'
   * glyphs-vs-pinyin tile front).
   */
  children?: ReactNode;
} & Omit<ToolbarPopoverButtonProps, "accessibilityLabel" | "children" | "name" | "title">;

/**
 * One header control for the two "how should glyphs look" session settings — tone colors and
 * Han script — which were previously a pair of push buttons the reader had to decode icon-first.
 *
 * It opens a popover rather than a menu, so each setting takes its natural control: tone colors
 * is a switch and the script is a pair of chips. Both write ABSOLUTE values rather than toggling
 * relative to the persisted user setting. The old colors button stored `!userSetting` and treated
 * any non-null override as its pushed state, so it painted itself active even when the override
 * happened to agree with the setting; a control that shows the resolved value directly can no
 * longer disagree with it. `null` (inherit) stays reachable through the Settings screen, which is
 * the only place that edits the persisted value.
 *
 * Pass `label` for the toolbar tray's icon-and-label pill; without it the control is the circular
 * push button of the header expansion strip. Both open the same popover. Callers may pass
 * `children` for page-local rows that belong in the same Display popover.
 */
export function PrimaryControlsStripExpansionDisplayPushButton({
  children,
  ...pushButtonProps
}: PrimaryControlsStripExpansionDisplayPushButtonProps) {
  const isToneColorsEnabled = useEffectiveIsToneColorsEnabled();
  const effectiveScript = useEffectiveHanziScript();
  const [setIsToneColorsEnabledOverride, setHanziScriptOverride] = useContextSelectors(
    NavContext,
    (c) => c.setIsToneColorsEnabledOverride,
    (c) => c.setHanziScriptOverride,
  );

  return (
    <ToolbarPopoverButton
      accessibilityLabel={ACCESSIBILITY_LABEL}
      name="MdTune"
      {...pushButtonProps}
    >
      <ToolbarPopoverSwitchRow
        checked={isToneColorsEnabled}
        label="Tone colors"
        onCheckedChange={setIsToneColorsEnabledOverride}
        testID="display-tone-colors"
      />
      <ToolbarPopoverChoiceRow
        label="Script"
        onValueChange={setHanziScriptOverride}
        options={SCRIPT_OPTIONS}
        value={effectiveScript}
      />
      {children}
    </ToolbarPopoverButton>
  );
}
